<template>
  <div class="thank-you-container">
    <div class="content-wrapper">
      <div class="expired-icon">×</div>
      <h1 class="title">This Contract has Expired.</h1>

      <div v-if="isLoading" class="spinner"></div>

      <div v-if="!isLoading && contract.id">
        <div v-if="contract.contract_reissue">
          <div v-if="contract.renewal_token">
            
            <p class="subtitle" v-if="isReissue">Contract was expired for more than 30 days. Would you like the Contract to be re-issued?</p>
            <p class="subtitle" v-else>Would you like the Contract to be extended?</p>

            <div class="actions">
              <button class="button no" @click="handleDeclineReissue">No</button>
              <button class="button yes" @click="handleAcceptReissue">Yes</button>
            </div>
          </div>
          
          <div v-else>
            <p class="subtitle" v-if="isReissue">This contract is no longer eligible for re-issue. Please contact us for further assistance.</p>
            <p class="subtitle" v-else>This contract is no longer eligible for extension. Please contact us for further assistance.</p>
          </div>
          </div>

        <div v-else>
          <p class="subtitle" v-if="isReissue">You have declined to have this contract re-issued. Please contact us for further assistance.</p>
          <p class="subtitle" v-else>You have declined to have this contract extended. Please contact us for further assistance.</p>
        </div>
        </div>
      </div>
    <img :src="logo" alt="Vertex Cyber Security Logo" class="logo">
  </div>
</template>

<script>
import vertexLogo from '@/assets/img/vertex_logo.png';

export default {
  name: 'ClientContractExpired',
  props: {
    unique_key: {
      type: String,
      required: true,
    },
    access_key: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      logo: vertexLogo,
      isLoading: true,
      contract: {}, // To store contract details
      // This flag controls the text
      isReissue: false, // Default to extension
    };
  },
  created() {
    this.getContractDetails();
  },
  methods: {
    async getContractDetails() {
      this.isLoading = true;
      try {
        const response = await this.$axios_internal.get(`/contract/view-contract/${this.unique_key}/${this.access_key}`);
        if (!response.data.vertex_contract) {
          // If for some reason it's invalid, send to the deleted page
          this.$router.push({ name: 'ContractDeletedNonExist' });
          return;
        }
        this.contract = response.data.vertex_contract;

        // --- MODIFIED LOGIC ---
        // Check if the renewal token is valid (exists and is not expired)
        const isTokenValid = this.checkTokenValidity(this.contract.renewal_token_expires_at);

        if (isTokenValid) {
          // Token is valid (not expired), so it's an "extension"
          this.isReissue = false;
        } else {
          // Token is invalid (expired or null), so it's a "re-issue"
          this.isReissue = true;
        }
        // --- END MODIFIED LOGIC ---

      } catch (error) {
        // this.$toast.error("Could not load contract details.");
        this.$router.push({ name: 'ContractDeletedNonExist' });
      } finally {
        this.isLoading = false;
      }
    },

    // --- NEW METHOD ---
    checkTokenValidity(utcDateString) {
      // If the field is null, undefined, or empty, it's not valid.
      if (!utcDateString) {
        return false;
      }
      
      const expiryDate = new Date(utcDateString);
      const now = new Date();
      
      // The token is valid if its expiry date is *after* the current time.
      return expiryDate > now;
    },
    // --- `CalculateDaysPastExpiry` method removed ---

    handleAcceptReissue() {
      if (this.contract.renewal_token) {
        const token = this.contract.renewal_token;
        window.location.href = `/renew-contract/${this.unique_key}/${this.access_key}?token=${token}`;
      } else {
        this.$toast.error("Renewal/Extension token is missing. Please contact support.");
      }
    },

    async handleDeclineReissue() {
      this.isLoading = true;
      //this.$toast.info("Updating contract status...");
      try {
        await this.$axios_internal.post(`/contracts/decline-reissue`, { 
           unique_key: this.unique_key, 
           access_key: this.access_key 
        });
        
        // Update the local state to show the "declined" message
        this.contract.contract_reissue = false; 
        this.$toast.success("Your preference has been saved.");
        this.$router.push({ name: 'ContractFeedback', params: { unique_key: this.unique_key, access_key: this.access_key } })
      } catch (error) {
        this.$toast.error(error.response?.data?.message || "Could not update the contract. Please try again.");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Using a combination of styles from ContractDeleted.vue and your reference image */
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');

.thank-you-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-image: url("~@/assets/img/BG_Mountain.jpg");
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  font-family: 'Roboto', sans-serif;
  text-align: center;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 50px;
  background-color: rgba(255, 255, 255, 0.8); /* Slightly more opaque */
  padding: 40px 60px;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  min-width: 500px;
}

.expired-icon {
  font-size: 5rem;
  font-weight: 200;
  color: #d9534f; /* Red color for the X */
  line-height: 1;
  margin-bottom: 10px;
}

.title {
  font-size: 2.2rem;
  font-weight: 500;
  color: #333;
  margin: 10px 0;
}

.subtitle {
  font-size: 1.1rem;
  color: #555;
  margin-top: 10px;
  margin-bottom: 25px;
}

.actions {
  display: flex;
  gap: 15px; /* Space between buttons */
  justify-content: center;
}

.button {
  padding: 12px 30px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  color: white;
  transition: opacity 0.2s ease;
}

.button:hover {
  opacity: 0.9;
}

.button.no {
  background-color: #f0ad4e; /* Orange for 'No' */
}

.button.yes {
  background-color: #00a0c1; /* Teal for 'Yes' */
}

.logo {
  max-width: 250px;
}

.spinner {
  border: 5px solid #f3f3f3;
  border-top: 5px solid #00a0c1;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
